<script>
  import Heading from './Heading.svelte';
  import { client } from './lib/client';
  import { createQuery } from '@tanstack/svelte-query';
  import { _ } from 'svelte-i18n';
  import {
    Card,
    Button,
    Tabs,
    TabItem,
    Table,
    TableHeadCell,
    TableHead,
    TableBody,
    TableBodyRow,
    Select,
    TableBodyCell,
    PaginationItem,
    Popover,
    Spinner,
    ListPlaceholder,
    Skeleton,
    Input,
    Toast,
    Tooltip,
    Dropdown,
    DropdownItem,
    Progressbar,
    Checkbox,
  } from 'flowbite-svelte';
  import {
    PlusSolid,
    ArrowLeftOutline,
    ArrowRightOutline,
    CashOutline,
    ClockOutline,
    CheckPlusCircleOutline,
    SearchOutline,
    RocketOutline,
    CloseCircleSolid,
    UserSolid,
    InfoCircleOutline,
    ChevronDownSolid,
  } from 'flowbite-svelte-icons';
  import { fade, fly, slide } from 'svelte/transition';
  import { flip } from 'svelte/animate';
  import { sineIn } from 'svelte/easing';
  import FallbackImage from './FallbackImage.svelte';
  import * as Sentry from '@sentry/svelte';
  import { sendDebugEvent } from './debug';
  import cls from 'classnames';

  let highPriorityCampaignsPage = 1;
  const highPriorityCampaignsPageSize = 60;
  let highPriorityCampaignLoadingIndex = {
    loadingIdx: null,
    intent: null,
    optInAll: false,
  };

  let toastMessage = '';
  let toastType = 'success';
  $: toastVisible = false;
  let startIdx = 0;

  $: openTab = 'for-you'; // 'for-you' 'discover' 'my-campaigns'

  let eligibleASINsPage = 1;
  let eligibleASINsPageSize = 5;
  let eligibleASINsSearch = '';

  function onEligibleASINsSearch() {
    eligibleASINsPage = 1;
  }

  let discoverCampaignsPage = 1;
  let discoverCampaignsPageSize = 20;
  let discoverCampaignsPageSearch = '';
  let discoverCampaignsLoadingIdx = null;

  function onDiscoverSearchChange() {
    discoverCampaignsPage = 1;
  }

  let myCampaignsPage = 1;
  let myCampaignsPageSize = 20;
  let myCampaignsPageSearch = '';

  function onMyCampaignsPageSearch() {
    myCampaignsPage = 1;
  }

  let innerHeight = 0;
  let innerWidth = 0;

  let sortKey = null;
  let sortDir = 'desc';
  let campaignStatusFilter = null;

  let discoverCategoryFilter = [];
  let discoverMyBrandsFilter = false;

  const discoverCategoryFilterOptions = [
    {
      display: 'Automotive',
      name: 'Automotive',
    },
    {
      display: 'Fashion and Luxury',
      name: 'Fashion and Luxury',
    },
    {
      display: 'Technology',
      name: 'Technology',
    },
    {
      display: 'Home Essentials',
      name: 'Home Essentials',
    },
    {
      display: 'Media and Entertainment',
      name: 'Media and Entertainment',
    },
    {
      display: 'Office',
      name: 'Office',
    },
    {
      display: 'Health and Beauty',
      name: 'Health and Beauty',
    },
    {
      display: 'Sports',
      name: 'Sports',
    },
    {
      display: 'Digital Products',
      name: 'Digital Products',
    },
    {
      display: 'Gift Cards and Credit Cards',
      name: 'Gift Cards and Credit Cards',
    },
    {
      display: 'Other',
      name: 'Other',
    },
  ];

  function toggleDiscoverCategoryFilter(filterName) {
    if (discoverCategoryFilter.includes(filterName)) {
      discoverCategoryFilter = discoverCategoryFilter.filter(
        (entry) => entry !== filterName,
      );
    } else {
      discoverCategoryFilter = [filterName, ...discoverCategoryFilter];
    }
  }

  function triggerSort(newSortKey) {
    if (sortKey === newSortKey) {
      if (sortDir === 'desc') {
        sortDir = 'asc';
      } else {
        sortKey = null;
        sortDir = 'desc';
      }
    } else {
      sortKey = newSortKey;
      sortDir = 'desc';
    }
  }

  function formatDate(date) {
    const year = date.getFullYear() % 100;

    const month = (1 + date.getMonth()).toString();
    const day = date.getDate().toString();

    return `${month}/${day}/${year}`;
  }

  /**
   *
   * @param tabname {string} the name of the tab the user is visiting 'for-you' 'discover' 'my-campaigns'
   */
  function sendDebugTabVisit(tabname) {
    sendDebugEvent(`cc-visit-${tabname}`);
  }

  sendDebugTabVisit('for-you');

  $: eligibleAsinsQuery = createQuery({
    queryKey: [
      'eligible-asins',
      {
        eligibleASINsPage,
        eligibleASINsPageSize,
        eligibleASINsSearch,
        sortDir,
        sortKey,
      },
    ],
    queryFn: () =>
      getEligibleASINs(
        eligibleASINsSearch,
        sortKey,
        sortDir,
        eligibleASINsPage,
        eligibleASINsPageSize,
      ),
  });
  $: highPriorityCampaigns = createQuery({
    queryKey: [
      'high-priority-campaign-asins',
      {
        highPriorityCampaignsPage,
        highPriorityCampaignsPageSize,
      },
    ],
    queryFn: () =>
      fetchHighPriorityCampaigns(
        highPriorityCampaignsPage,
        highPriorityCampaignsPageSize,
      ),
  });

  async function fetchDiscoverCampaigns(
    searchValue,
    sortKey,
    sortDir,
    page = 1,
    pageSize = 5,
    discoverCategoryFilter = [],
    discoverMyBrandsFilter = false,
  ) {
    const queryString = new URLSearchParams({
      page,
      pageSize,
    });

    if (searchValue && searchValue !== '') {
      queryString.set('searchValue', searchValue);
    }

    if (sortKey && sortDir) {
      queryString.set('sortBy', sortKey);
      queryString.set('desc', sortDir === 'desc');
    }

    discoverCategoryFilter.forEach((elem) => {
      queryString.append('category', elem);
    });

    if (discoverMyBrandsFilter) {
      queryString.set('myBrands', discoverMyBrandsFilter);
    }

    let result;
    try {
      result = await client.get(
        `/api/get-discover-cc-campaigns?${queryString.toString()}`,
      );
    } catch (e) {
      console.error(e);
    }

    return result.data.object;
  }

  $: discoverQuery = createQuery({
    queryKey: [
      'discover-campagins',
      {
        discoverCampaignsPageSearch,
        sortKey,
        sortDir,
        discoverCampaignsPage,
        discoverCampaignsPageSize,
        discoverCategoryFilter,
        discoverMyBrandsFilter,
      },
    ],
    queryFn: () =>
      fetchDiscoverCampaigns(
        discoverCampaignsPageSearch,
        sortKey,
        sortDir,
        discoverCampaignsPage,
        discoverCampaignsPageSize,
        discoverCategoryFilter,
        discoverMyBrandsFilter,
      ),
  });

  async function fetchMyCampaigns(
    searchValue,
    sortKey,
    sortDir,
    page = 1,
    pageSize = 5,
    campaignStatusFilter = null,
  ) {
    const queryString = new URLSearchParams({
      page,
      pageSize,
    });

    if (searchValue && searchValue !== '') {
      queryString.set('searchValue', searchValue);
    }

    if (sortKey && sortDir) {
      queryString.set('sortBy', sortKey);
      queryString.set('desc', sortDir === 'desc');
    }

    if (campaignStatusFilter) {
      queryString.set('status', campaignStatusFilter);
    }

    let result;
    try {
      result = await client.get(
        `/api/get-my-cc-campaigns?${queryString.toString()}`,
      );
    } catch (e) {
      console.error(e);
    }

    return result.data.object;
  }

  $: myCampaignsQuery = createQuery({
    queryKey: [
      'my-campagins',
      {
        myCampaignsPageSearch,
        sortKey,
        sortDir,
        myCampaignsPage,
        myCampaignsPageSize,
        campaignStatusFilter,
      },
    ],
    queryFn: () =>
      fetchMyCampaigns(
        myCampaignsPageSearch,
        sortKey,
        sortDir,
        myCampaignsPage,
        myCampaignsPageSize,
        campaignStatusFilter,
      ),
  });

  async function paginateHighPriorityCampaigns(newStartIdx) {
    if (newStartIdx < 0) {
      return;
    }
    if (newStartIdx >= $highPriorityCampaigns.data?.data.length) {
      highPriorityCampaignsPage += 1;
      const s = await $highPriorityCampaigns.refetch();
      if (!s.data?.length) {
        highPriorityCampaignsPage -= 1;
        await $highPriorityCampaigns.refetch();
      }
      newStartIdx = 0;
    }
    startIdx = newStartIdx;
  }

  async function updateCCStatus(
    campaignId,
    asins,
    status,
    loadingIdx,
    debugAction,
  ) {
    highPriorityCampaignLoadingIndex = {
      loadingIdx,
      intent: status,
    };
    let err;
    try {
      let page;
      if (debugAction === 'discover') {
        page = discoverCampaignsPage;
      }
      await client.patch('/api/update-cc-campaign', {
        externalCampaignId: campaignId,
        asins,
        status,
        extra: {
          debugAction,
          ...(page ? { page } : {}),
        },
      });
    } catch (e) {
      console.error(e);
      err = true;
    } finally {
      await $highPriorityCampaigns.refetch();
      highPriorityCampaignLoadingIndex = {
        loadingIdx: null,
        intent: null,
      };
      if (err) {
        errorToast(
          `Failed to ${status === 'OPTED-IN' ? 'opt-in to' : 'remove'} campaign.`,
        );
      }
    }
  }

  // eslint-disable-next-line
  async function optInAllCampaigns() {
    highPriorityCampaignLoadingIndex = {
      loadingIdx: null,
      intent: null,
      optInAll: true,
    };
    let err;
    let optInAllResults;
    try {
      optInAllResults = await client.post('/api/opt-in-all-cc-campaigns');
      optInAllResults = optInAllResults.data.object;
    } catch (e) {
      err = true;
      Sentry.captureException(e);
    } finally {
      await $highPriorityCampaigns.refetch();
      highPriorityCampaignLoadingIndex = {
        loadingIdx: null,
        intent: null,
        optInAll: false,
      };
      if (!err && optInAllResults.successCount > 0) {
        successToast(
          `Success! You've been opted in to ${optInAllResults.successCount} out of ${optInAllResults.successCount + optInAllResults.errorCount} campaigns.`,
        );
      } else {
        errorToast(`Something went wrong.`);
      }
    }
  }

  async function fetchHighPriorityCampaigns(page = 1, pageSize = 3) {
    const queryString = new URLSearchParams({
      page,
      pageSize,
    });

    let result;

    try {
      result = await client.get(
        `/api/get-high-priority-cc-campaigns?${queryString.toString()}`,
      );
    } catch (e) {
      console.error(e);
    }

    return result.data.object;
  }

  function myCampaignsCreateLink(campaignName) {
    changeTab('for-you');
    eligibleASINsSearch = campaignName;
    setTimeout(() => {
      document.getElementById('eligible-asins-scroll').scrollIntoView();
    }, 100);
  }

  function changeTab(tabName) {
    sendDebugTabVisit(tabName);
    openTab = tabName;

    // reset queries when changing the tab
    eligibleASINsPage = 1;
    eligibleASINsPageSize = 5;
    sortKey = null;
    sortDir = 'desc';
    eligibleASINsSearch = '';
    discoverCampaignsLoadingIdx = null;
    discoverCampaignsPage = 1;
    discoverCampaignsPageSearch = '';
    discoverCampaignsPageSize = 20;
  }

  async function getEligibleASINs(
    searchValue,
    sortKey,
    sortDir,
    page = 1,
    pageSize = 5,
  ) {
    const queryString = new URLSearchParams({
      page,
      pageSize,
    });

    if (searchValue && searchValue !== '') {
      queryString.set('searchValue', searchValue);
    }

    if (sortKey && sortDir) {
      queryString.set('sortBy', sortKey);
      queryString.set('desc', sortDir === 'desc');
    }

    let result;
    try {
      result = await client.get(
        `/api/get-eligible-cc-asins?${queryString.toString()}`,
      );
    } catch (e) {
      console.error(e);
    }

    return result.data.object;
  }

  const rowsPerPageSelect = [
    { value: 5, name: '5' },
    { value: 10, name: '10' },
    { value: 20, name: '20' },
    { value: 50, name: '50' },
  ];

  /**
   * Change the number of cards we are rendering based on screen size. Values are based on
   * tailwind breakpoints: https://tailwindcss.com/docs/responsive-design.
   *
   * Since there is a point where the sidebar transitions from mobile to desktop (@ 768px) we actually have
   * __less__ screen space due to the sidebar, so we need to reduce the number of cards reactively.
   *
   * @param innerWidth reactive value of the innerWidth from the <svelte:window /> tag
   * @returns the number of cards to render
   */
  function reactiveSizeHighPriorityCampaigns(innerWidth) {
    if (innerWidth < 640) {
      return 1;
    }
    if (innerWidth < 768) {
      return 2;
    }
    if (innerWidth < 1024) {
      return 1;
    }
    if (innerWidth < 1280) {
      return 2;
    }
    return 3;
  }

  $: highPriority = reactiveSizeHighPriorityCampaigns(innerWidth);

  function errorToast(message) {
    toastVisible = true;
    toastMessage = message;
    toastType = 'error';
  }

  function successToast(message) {
    toastVisible = true;
    toastMessage = message;
    toastType = 'success';
  }

  function updateCampaignStatusFilter(newStatus) {
    campaignStatusFilter = newStatus;
  }

  function toggleMyBrands() {
    discoverMyBrandsFilter = !discoverMyBrandsFilter;
  }

  function getStatus(state) {
    const status = {
      NOT_STARTED: {
        status: 'not started',
        btnClass: 'bg-campaignStatusNotStarted',
        tip: $_('components.creatorConnections.status.notStarted.tip', {
          default: 'This campaign has not yet gone live.',
        }),
        note: $_('components.creatorConnections.status.notStarted.note', {
          default: 'Status updated every 24 hours',
        }),
      },
      LIVE: {
        status: 'live',
        btnClass: 'bg-campaignStatusLive',
        tip: $_('components.creatorConnections.status.live.tip', {
          default: 'This campaign is live.',
        }),
        note: $_('components.creatorConnections.status.live.note', {
          default: 'Status updated every 24 hours',
        }),
      },
      ENDED: {
        status: 'ended',
        btnClass: 'bg-campaignStatusEnded',
        tip: $_('components.creatorConnections.status.ended.tip', {
          default: 'This campaign has ended.',
        }),
        note: $_('components.creatorConnections.status.ended.note', {
          default: 'Status updated every 24 hours',
        }),
      },
    };
    return status[state] ?? status.LIVE;
  }
</script>

{#if toastVisible}
  <Toast
    transition={slide}
    color={toastType === 'success' ? 'green' : 'red'}
    position="top-right"
    class="z-10"
    bind:toastStatus={toastVisible}
    on:close={() => {
      toastVisible = false;
    }}
  >
    <svelte:fragment slot="icon">
      {#if toastType === 'success'}
        <CheckPlusCircleOutline class="text-btnInputSuccess" />
      {:else}
        <CloseCircleSolid class="text-btnInputError" />
      {/if}
    </svelte:fragment>

    {toastMessage}
  </Toast>
{/if}

<svelte:window bind:innerHeight bind:innerWidth />

<Tabs>
  <TabItem
    open={openTab === 'for-you'}
    title="For You"
    on:click={() => changeTab('for-you')}
  >
    <div slot="title" class="flex items-center gap-2">
      <RocketOutline />
      For You
    </div>
    <div transition:fly class="-mx-4 flex flex-col gap-6 sm:mx-0">
      <Card size="xl" class="w-full max-w-none">
        <header class="flex flex-col items-start justify-between lg:flex-row">
          <div>
            <Heading level={3} class="text-xl font-bold">
              {$_('creatorConnnections.highPriorityCampaigns.heading', {
                default:
                  'High Priority Campaigns - Opt in now to earn instantly',
              })}
            </Heading>
            <p class="max-w-[60ch] text-sm text-gray-500">
              {$_('creatorConnnections.highPriorityCampaigns.description', {
                default:
                  'You are already getting clicks on ASINs that are in these campaigns. Opt in now to earn on those clicks.',
              })}
            </p>
          </div>
          <div class="flex items-center gap-6">
            <div class="hidden gap-4 lg:flex">
              <button
                on:click={paginateHighPriorityCampaigns(startIdx - 1)}
                disabled={startIdx === 0}
                class="text-gray-500 disabled:text-gray-300"
              >
                <ArrowLeftOutline />
              </button>
              <button
                class="text-gray-500 disabled:text-gray-300"
                on:click={paginateHighPriorityCampaigns(startIdx + 1)}
                disabled={startIdx + highPriority >=
                  $highPriorityCampaigns.data?.totalCount}
              >
                <ArrowRightOutline />
              </button>
            </div>
          </div>
        </header>
        <main class="mt-4">
          <div class="flex items-center gap-4">
            {#if $highPriorityCampaigns.isLoading}
              {#each { length: highPriority } as idx}
                <article
                  id={`high-priority-campaigns-article-${idx}`}
                  class="h-[287px] flex-1 rounded-lg bg-[#FAFAFA] p-4"
                >
                  <Skeleton size="xs" />
                </article>
              {/each}
            {:else if $highPriorityCampaigns.data?.data.length > 0}
              {#each $highPriorityCampaigns.data.data.slice(startIdx, startIdx + highPriority) as campaign, idx (campaign.campaign_id)}
                <article
                  animate:flip={{ duration: 200, easing: sineIn }}
                  class="flex-1 rounded-lg bg-[#FAFAFA] p-4 transition-all lg:max-w-[476px]"
                  in:fade={{ delay: 200, duration: 100 }}
                >
                  <div>
                    <div class="flex items-start gap-2">
                      <FallbackImage
                        src={campaign.image_url}
                        height="72"
                        width="72"
                        class="h-[72px] w-[72px] object-contain"
                        alt={campaign.campaign_name}
                      />
                      <div class="py-1">
                        <h5
                          class="line-clamp-1 text-sm font-normal text-gray-500"
                        >
                          {campaign.brand_name}
                        </h5>
                        <p
                          class="line-clamp-2 text-sm font-normal text-gray-900"
                        >
                          {campaign.campaign_name}
                        </p>
                      </div>
                    </div>
                    <dl class="mb-3 mt-2">
                      <div class="flex gap-1">
                        <dt class="flex items-center gap-1">
                          <CashOutline size="xs" class="text-gray-500" />
                          <span class="text-sm font-normal">
                            {$_(
                              'creatorConnnections.highPriorityCampaigns.cards.commissionRateLabel',
                              {
                                default: 'Commission Rate:',
                              },
                            )}
                          </span>
                        </dt>
                        <dd class="text-sm font-normal">
                          {campaign.commission_rate}%
                        </dd>
                      </div>
                      <div class="flex gap-1">
                        <dt class="flex items-center gap-1">
                          <ClockOutline size="xs" class="text-gray-500" />
                          <span class="text-sm font-normal">
                            {$_(
                              'creatorConnnections.highPriorityCampaigns.cards.endDateLabel',
                              {
                                default: 'Ends',
                              },
                            )}
                          </span>
                        </dt>
                        <dd class="text-sm font-normal">
                          {new Date(
                            campaign.campaign_end_date,
                          ).toLocaleDateString('en-US', {
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                          })}
                        </dd>
                      </div>
                    </dl>
                    <div class="mb-4 flex items-center gap-2">
                      <Button
                        size="xs"
                        class="flex items-center gap-1"
                        on:click={() => {
                          window.open(
                            `https://affiliate-program.amazon.com/p/connect/requests?status=opportunity&sortBy=recommended_for_you&keyword=${encodeURIComponent(campaign.campaign_name)}&campaignStatuses=active%2Cpending`,
                            '_blank',
                          );
                          updateCCStatus(
                            campaign.campaign_id,
                            campaign.asins,
                            'OPTED-IN',
                            idx,
                            'high-priority',
                          );
                        }}
                      >
                        {#if highPriorityCampaignLoadingIndex.loadingIdx === idx && highPriorityCampaignLoadingIndex.intent === 'OPTED-IN'}
                          <Spinner size="4" />
                        {/if}
                        <PlusSolid size="xs" />
                        <span
                          >{$_(
                            'creatorConnections.highPriorityCampaigns.cards.optInButton',
                            {
                              default: 'Opt in',
                            },
                          )}
                        </span>
                      </Button>
                      <Button
                        size="xs"
                        color="alternative"
                        class="flex items-center gap-1"
                        on:click={() => {
                          updateCCStatus(
                            campaign.campaign_id,
                            campaign.asins,
                            'NOT-INTERESTED',
                            idx,
                            'high-priority',
                          );
                        }}
                      >
                        {#if highPriorityCampaignLoadingIndex.loadingIdx === idx && highPriorityCampaignLoadingIndex.intent === 'NOT-INTERESTED'}
                          <Spinner size="4" />
                        {/if}
                        <span>
                          {$_(
                            'creatorConnnections.highPriorityCampaigns.cards.notInterestedButton',
                            {
                              default: 'Not Interested',
                            },
                          )}
                        </span>
                      </Button>
                    </div>
                    <hr class="-mx-4 my-4" />
                    <dl class="flex flex-col gap-2">
                      <div class="flex items-center justify-between">
                        <dt class="text-xs">
                          {$_(
                            'creatorConnnections.highPriorityCampaigns.cards.clicksLabel',
                            {
                              default: 'CLICKS (last 7 days)',
                            },
                          )}
                        </dt>
                        <dd class="flex items-center gap-1 text-[#1C64F2]">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clip-path="url(#clip0_2060_5798)">
                              <path
                                d="M1.62245 4.61853L3.32945 5.07592"
                                stroke="#1C64F2"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                              <path
                                d="M2.3474 8.72556L3.59701 7.47595"
                                stroke="#1C64F2"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                              <path
                                d="M4.61841 1.62244L5.0758 3.32944"
                                stroke="#1C64F2"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                              <path
                                d="M8.72546 2.34753L7.47585 3.59715"
                                stroke="#1C64F2"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                              <path
                                d="M5.7535 6.41862C5.60082 8.05247 6.10872 11.3669 8.35398 14.067C8.70202 14.4854 9.27359 14.4467 9.49154 13.9478C9.73803 13.3837 9.99695 12.6452 10.2058 12.0086L12.2014 14.0043C12.6992 14.5021 13.5062 14.5021 14.004 14.0043C14.5018 13.5065 14.5018 12.6995 14.0041 12.2017L12.0084 10.2059C12.6451 9.99711 13.3836 9.73816 13.9477 9.49167C14.4465 9.27372 14.4853 8.70215 14.0667 8.35411C11.3667 6.10886 8.05232 5.60096 6.41848 5.75364C6.06091 5.78706 5.78692 6.06105 5.7535 6.41862Z"
                                stroke="#1C64F2"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2060_5798">
                                <rect width="16" height="16" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span>
                            {new Intl.NumberFormat('en-US').format(
                              campaign.clicks,
                            )}
                            {$_(
                              'creatorConnnections.highPriorityCampaigns.cards.clicksValue',
                              {
                                default: 'clicks',
                              },
                            )}
                          </span>
                        </dd>
                      </div>
                      <div class="flex items-center justify-between">
                        <dt class="text-xs">
                          {$_(
                            'creatorConnnections.highPriorityCampaigns.cards.ASINsLabel',
                            { default: 'ASIN' },
                          )}
                        </dt>
                        <dd class="text-sm font-normal">
                          {`${campaign.asins[0]}${campaign.asins[1] ? `, ${campaign.asins[1]}...` : ''}`}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </article>
              {/each}
            {:else}
              <p
                class="h-48 w-full content-center rounded-md bg-gray-100 text-center"
              >
                Your current live links don't match to any eligible Creator
                Connections campaigns. Click 'discover' to find more campaigns!
              </p>
            {/if}
          </div>
          {#if $highPriorityCampaigns.data?.data.length > 0}
            <div
              class="mt-4 flex justify-between gap-4 justify-self-center lg:hidden"
            >
              <button
                on:click={paginateHighPriorityCampaigns(startIdx - 1)}
                disabled={startIdx === 0}
                class="text-gray-500 disabled:text-gray-300"
              >
                <ArrowLeftOutline />
              </button>
              <button
                class="text-gray-500 disabled:text-gray-300"
                on:click={paginateHighPriorityCampaigns(startIdx + 1)}
                disabled={startIdx + highPriority >=
                  $highPriorityCampaigns.data?.totalCount}
              >
                <ArrowRightOutline />
              </button>
            </div>
          {/if}
        </main>
      </Card>
      <Card size="xl" class="w-full max-w-none">
        <div class="mb-4 flex items-center justify-between">
          <div class="flex w-full flex-wrap justify-between gap-8">
            <div id="eligible-asins-scroll" class="flex flex-col">
              <Heading level={3} class="text-xl font-bold">
                {$_('creatorConnections.eligibleASINs.heading', {
                  default: 'Eligible ASINs for Your Opted-Into Campaigns',
                })}
              </Heading>
              <p class="max-w-[60ch] text-sm text-gray-500">
                Here you can easily discover ASINs across your opted-into
                campaigns!
              </p>
            </div>
            <div>
              <Input
                size="lg"
                class="sm:w-[350px]"
                bind:value={eligibleASINsSearch}
                on:input={onEligibleASINsSearch}
                placeholder={$_(
                  'creatorConnections.eligibleASINs.searchPlaceholder',
                  {
                    default: 'Search by Campaign, ASIN, or Brand',
                  },
                )}
              >
                <SearchOutline slot="left" class="h-5 w-5" />
              </Input>
            </div>
          </div>
        </div>
        <div class="-mx-4 sm:-mx-6">
          <Table class="border-b">
            <TableHead>
              <TableHeadCell>
                {$_('creatorConnections.eligibleASINs.table.brandHeading', {
                  default: 'Brand',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.eligibleASINs.table.imageHeading', {
                  default: 'Image',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.eligibleASINs.table.ASIN', {
                  default: 'ASIN',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.eligibleASINs.table.campaignName', {
                  default: 'Campaign Name',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.eligibleASINs.table.date', {
                  default: 'Date',
                })}
              </TableHeadCell>
              <TableHeadCell>
                <div class="flex items-center gap-1">
                  <div>Budget Remaining</div>
                  <div>
                    <InfoCircleOutline class="h-3 w-3" />
                  </div>
                  <Tooltip
                    type="light"
                    class="absolute max-w-[30ch] normal-case"
                  >
                    The progress bar below updates daily and will show you how
                    much campaign budget in total is remaining - hover over for
                    more details!
                  </Tooltip>
                </div>
              </TableHeadCell>
              <TableHeadCell class="flex items-center">
                <span>
                  {$_('creatorConnections.eligibleASINs.table.commissionRate', {
                    default: 'Comm. Rate',
                  })}
                </span>
                <button
                  name="name"
                  on:click={() => triggerSort('commissionRate')}
                  class={`ml-1 hover:text-buttonBlue ${sortKey === 'commissionRate' ? 'text-sm' : 'text-lg'} h-8 w-2 text-gray-500`}
                >
                  {#if sortKey === 'commissionRate' && sortDir === 'desc'}
                    &downarrow;
                  {:else if sortKey === 'commissionRate' && sortDir === 'asc'}
                    &uparrow;
                  {:else}
                    &updownarrow;
                  {/if}
                </button>
              </TableHeadCell>
              <TableHeadCell>See on Amazon</TableHeadCell>
            </TableHead>
            <TableBody>
              {#if $eligibleAsinsQuery.isLoading}
                <TableBodyRow class="h-[200px]">
                  <ListPlaceholder
                    class="absolute h-[200px] w-full max-w-[unset] overflow-hidden rounded-none border-none"
                  />
                </TableBodyRow>
              {:else if !$eligibleAsinsQuery.data?.data?.length > 0}
                <TableBodyRow class="h-[200px]">
                  <div
                    class="absolute h-[200px] w-full bg-white text-center font-medium leading-[200px]"
                  >
                    <span>
                      {$_('creatorConnections.eligibleASINs.table.empty', {
                        default:
                          'Opt-in to a Creator Connections campaign to easily find and create links in this section for other ASINs in those campaigns.',
                      })}
                    </span>
                  </div>
                </TableBodyRow>
              {:else}
                {#each $eligibleAsinsQuery.data.data as eligibleAsin}
                  <TableBodyRow>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {eligibleAsin.brand_name}
                    </TableBodyCell>
                    <TableBodyCell>
                      <img
                        src={eligibleAsin.image_url}
                        height="72"
                        width="72"
                        class="h-[72px] w-[72px] object-contain"
                        alt={eligibleAsin.brand_name}
                      />
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {eligibleAsin.asins}
                    </TableBodyCell>
                    <TableBodyCell
                      class="max-w-[300px] whitespace-normal text-wrap text-sm font-normal text-gray-500"
                    >
                      {eligibleAsin.campaign_name}
                    </TableBodyCell>
                    <TableBodyCell
                      class="whitespace-nowrap text-sm font-normal text-gray-500"
                    >
                      {formatDate(new Date(eligibleAsin.campaign_start_date))} -
                      {formatDate(new Date(eligibleAsin.campaign_end_date))}
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      <div class="group cursor-default pb-12">
                        <p
                          class="opacity-0 transition-all group-hover:opacity-100"
                        >
                          {new Intl.NumberFormat('en-US', {
                            currency: 'USD',
                            style: 'currency',
                            maximumFractionDigits: 0,
                          }).format(eligibleAsin.campaign_remaining_budget)}
                          /
                        </p>
                        <p
                          class="font-[900] opacity-0 transition-all group-hover:opacity-100"
                        >
                          {new Intl.NumberFormat('en-US', {
                            currency: 'USD',
                            style: 'currency',
                            maximumFractionDigits: 0,
                          }).format(eligibleAsin.campaign_budget)}
                        </p>
                        <div>
                          <Progressbar
                            progress={Math.floor(
                              (eligibleAsin.campaign_remaining_budget /
                                eligibleAsin.campaign_budget) *
                                100,
                            )}
                          />
                        </div>
                      </div>
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {eligibleAsin.campaign_commission_rate}%
                    </TableBodyCell>
                    <TableBodyCell>
                      <Button
                        on:click={() => {
                          sendDebugEvent('cc-view-amazon-product', {
                            extra: {
                              asin: eligibleAsin.asins,
                              url: `https://amazon.com/dp/${eligibleAsin.asins}`,
                            },
                          });
                          window.open(
                            `https://amazon.com/dp/${eligibleAsin.asins}`,
                            '_blank',
                          );
                        }}
                        size="xs"
                        class="flex gap-1"
                      >
                        Link
                      </Button>
                    </TableBodyCell>
                  </TableBodyRow>
                {/each}
              {/if}
            </TableBody>
          </Table>
          <div class="flex w-full flex-col items-center justify-center py-5">
            <div class="flex items-center">
              <div class="mr-2 text-sm text-gray-700">
                {$_('components.eligibleASINs.rows-per-page', {
                  default: 'Rows per page:',
                })}
              </div>
              <div class="mr-4">
                <Select
                  placeholder=""
                  size="sm"
                  class="mt-2"
                  items={rowsPerPageSelect}
                  bind:value={eligibleASINsPageSize}
                />
              </div>
              <!-- Help text -->
              {#if $eligibleAsinsQuery.data?.data?.length}
                <span class="text-sm text-gray-700 dark:text-gray-400">
                  {$_('components.eligibleASINs.showingPage-', {
                    default: 'Showing page ',
                  })}
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {eligibleASINsPage}
                  </span>
                  {$_('components.eligibleASINs.-of-', {
                    default: ' of ',
                  })}
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {Math.ceil(
                      $eligibleAsinsQuery.data.count / eligibleASINsPageSize,
                    )}
                  </span>
                </span>
              {/if}
            </div>
            {#if $eligibleAsinsQuery.data?.data?.length}
              <div class="flex w-full items-center justify-center pt-4">
                {#if eligibleASINsPage > 1}
                  <PaginationItem
                    normalClass="text-white bg-blue-600"
                    on:click={() => {
                      eligibleASINsPage -= 1;
                    }}
                  >
                    {$_('components.eligibleASINs.previous', {
                      default: 'Previous',
                    })}
                  </PaginationItem>
                {/if}
                {#if eligibleASINsPage < Math.ceil($eligibleAsinsQuery.data.count / eligibleASINsPageSize)}
                  <PaginationItem
                    normalClass="text-white bg-blue-600"
                    on:click={() => {
                      eligibleASINsPage += 1;
                    }}
                  >
                    {$_('components.eligibleASINs.next', {
                      default: 'Next',
                    })}
                  </PaginationItem>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      </Card>
    </div>
  </TabItem>
  <TabItem
    open={openTab === 'discover'}
    title="Discover"
    on:click={() => changeTab('discover')}
  >
    <div slot="title" class="flex items-center gap-2">
      <SearchOutline size="sm" />
      Discover
    </div>
    <div transition:fly class="-mx-4 flex flex-col gap-6 sm:mx-0">
      <Card size="xl" class="w-full max-w-none">
        <div class="mb-4 flex w-full flex-wrap justify-between gap-8">
          <div>
            <Heading level={3} class="text-xl font-bold">
              {$_('creatorConnections.discover.heading', {
                default: 'Discover New Campaigns',
              })}
            </Heading>
            <p class="text-sm text-gray-500">
              {$_('creatorConnections.discover.description', {
                default: 'Find new campaigns and products!',
              })}
            </p>
          </div>
          <div class="flex flex-row items-center gap-2">
            <div>
              <div class="mb-2">
                <Input
                  size="lg"
                  class="sm:w-[350px]"
                  bind:value={discoverCampaignsPageSearch}
                  on:input={onDiscoverSearchChange}
                  placeholder="Search by Campaign, ASIN, or Brand"
                >
                  <SearchOutline slot="left" class="h-5 w-5" />
                </Input>
              </div>
              <div class="flex items-center justify-end gap-4">
                <div class="flex items-center">
                  <Checkbox
                    checked={discoverMyBrandsFilter}
                    on:click={toggleMyBrands}
                  />
                  <span class="font-sm">My Brands</span>
                  <div id="my-brands-info-target" class="ml-1">
                    <InfoCircleOutline class="h-3 w-3" />
                  </div>
                  <Tooltip
                    triggeredBy="#my-brands-info-target"
                    trigger="hover"
                    class="w-[30ch] bg-[#2B3453]"
                  >
                    Check this box to view campaigns only from brands you've
                    opted into before.
                  </Tooltip>
                </div>
                <div>
                  <Button
                    color="outline"
                    class="flex gap-1 border border-gray-300"
                  >
                    Categories ({discoverCategoryFilter?.length || 'All'})
                    <ChevronDownSolid class="h-3 w-3" />
                  </Button>
                  <Dropdown class="w-min">
                    {#each discoverCategoryFilterOptions as filter}
                      <DropdownItem>
                        <div class="flex items-center">
                          <Checkbox
                            checked={discoverCategoryFilter.includes(
                              filter.name,
                            )}
                            on:click={() =>
                              toggleDiscoverCategoryFilter(filter.name)}
                          />
                          <span class="font-sm whitespace-nowrap">
                            {filter.display}
                          </span>
                        </div>
                      </DropdownItem>
                    {/each}
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="-mx-4 sm:-mx-6">
          <Table class="border-b">
            <TableHead>
              <TableHeadCell>
                {$_('creatorConnections.discover.table.campaignName', {
                  default: 'Campaign Name',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.discover.table.brand', {
                  default: 'Brand',
                })}
              </TableHeadCell>
              <TableHeadCell
                >{$_('creatorConnections.discover.table.image', {
                  default: 'Image',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.discover.table.date', {
                  default: 'Date',
                })}
              </TableHeadCell>
              <TableHeadCell>
                <div class="flex items-center gap-1">
                  <div>Budget Remaining</div>
                  <div>
                    <InfoCircleOutline class="h-3 w-3" />
                  </div>
                  <Tooltip
                    type="light"
                    class="absolute max-w-[30ch] normal-case"
                    >The progress bar below updates daily and will show you how
                    much campaign budget in total is remaining - hover over for
                    more details!</Tooltip
                  >
                </div>
              </TableHeadCell>
              <TableHeadCell>Category</TableHeadCell>
              <TableHeadCell class="flex items-center">
                <span>
                  {$_('creatorConnections.discover.table.commRate', {
                    default: 'Comm. Rate',
                  })}
                </span>
                <button
                  name="name"
                  on:click={() => triggerSort('commissionRate')}
                  class={`ml-1 hover:text-buttonBlue ${sortKey === 'commissionRate' ? 'text-sm' : 'text-lg'} h-8 w-2 text-gray-500`}
                >
                  {#if sortKey === 'commissionRate' && sortDir === 'desc'}
                    &downarrow;
                  {:else if sortKey === 'commissionRate' && sortDir === 'asc'}
                    &uparrow;
                  {:else}
                    &updownarrow;
                  {/if}
                </button>
              </TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableHead>
            <TableBody>
              {#if $discoverQuery.isLoading}
                <TableBodyRow class="h-[200px]">
                  <ListPlaceholder
                    class="absolute h-[200px] w-full max-w-[unset] overflow-hidden rounded-none border-none"
                  />
                </TableBodyRow>
              {:else if !$discoverQuery.data?.data?.length > 0}
                <TableBodyRow class="h-[200px]">
                  <div
                    class="absolute h-[200px] w-full bg-white text-center font-medium leading-[200px]"
                  >
                    <span>
                      {$_('creatorConnections.discover.table.empty', {
                        default:
                          "You aren't missing out on any earnings. Check back later!",
                      })}
                    </span>
                  </div>
                </TableBodyRow>
              {:else}
                {#each $discoverQuery.data.data as discoverCampaign, idx}
                  <TableBodyRow>
                    <TableBodyCell
                      class="max-w-[300px] whitespace-normal text-wrap text-sm font-normal text-gray-500"
                    >
                      {discoverCampaign.campaign_name}
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {discoverCampaign.brand_name}
                    </TableBodyCell>
                    <TableBodyCell>
                      <img
                        src={discoverCampaign.image_url}
                        height="72"
                        width="72"
                        class="h-[72px] w-[72px] object-contain"
                        alt={discoverCampaign.brand_name}
                      />
                    </TableBodyCell>
                    <TableBodyCell
                      class="whitespace-nowrap text-sm font-normal text-gray-500"
                    >
                      {formatDate(
                        new Date(discoverCampaign.campaign_start_date),
                      )} -
                      {formatDate(new Date(discoverCampaign.campaign_end_date))}
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      <div class="group cursor-default pb-12">
                        <div class="">
                          <p
                            class="opacity-0 transition-all group-hover:opacity-100"
                          >
                            {new Intl.NumberFormat('en-US', {
                              currency: 'USD',
                              style: 'currency',
                              maximumFractionDigits: 0,
                            }).format(
                              discoverCampaign.campaign_remaining_budget,
                            )}
                            /
                          </p>
                          <p
                            class="font-[900] opacity-0 transition-all group-hover:opacity-100"
                          >
                            {new Intl.NumberFormat('en-US', {
                              currency: 'USD',
                              style: 'currency',
                              maximumFractionDigits: 0,
                            }).format(discoverCampaign.campaign_budget)}
                          </p>
                        </div>
                        <div>
                          <Progressbar
                            progress={Math.floor(
                              (discoverCampaign.campaign_remaining_budget /
                                discoverCampaign.campaign_budget) *
                                100,
                            )}
                          />
                        </div>
                      </div>
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {discoverCampaign.campaign_category}
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {discoverCampaign.campaign_commission_rate}%
                    </TableBodyCell>
                    <TableBodyCell>
                      <Button
                        size="xs"
                        class="flex gap-1"
                        on:click={async () => {
                          try {
                            window.open(
                              `https://affiliate-program.amazon.com/p/connect/requests?status=opportunity&sortBy=recommended_for_you&keyword=${encodeURIComponent(discoverCampaign.campaign_name)}&campaignStatuses=active%2Cpending`,
                              '_blank',
                            );
                            discoverCampaignsLoadingIdx = idx;

                            await updateCCStatus(
                              discoverCampaign.campaign_id,
                              [discoverCampaign.asins],
                              'OPTED-IN',
                              null,
                              'discover',
                            );
                          } finally {
                            $discoverQuery.refetch();
                            discoverCampaignsLoadingIdx = null;
                          }
                        }}
                      >
                        {#if idx === discoverCampaignsLoadingIdx}
                          <Spinner size="4" />
                        {/if}
                        <PlusSolid size="xs" />
                        <span class="text-sm font-normal"
                          >{$_(
                            'creatorConnections.discover.table.optInButton',
                            {
                              default: 'Opt In',
                            },
                          )}
                        </span>
                      </Button>
                    </TableBodyCell>
                  </TableBodyRow>
                {/each}
              {/if}
            </TableBody>
          </Table>
          <div class="flex w-full flex-col items-center justify-center py-5">
            <div class="flex items-center">
              <div class="mr-2 text-sm text-gray-700">
                {$_('components.discover.rows-per-page', {
                  default: 'Rows per page:',
                })}
              </div>
              <div class="mr-4">
                <Select
                  placeholder=""
                  size="sm"
                  class="mt-2"
                  items={rowsPerPageSelect}
                  bind:value={discoverCampaignsPageSize}
                />
              </div>
              <!-- Help text -->
              {#if $discoverQuery.data?.data?.length}
                <span class="text-sm text-gray-700 dark:text-gray-400">
                  {$_('components.discover.showingPage-', {
                    default: 'Showing page ',
                  })}
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {discoverCampaignsPage}
                  </span>
                  {$_('components.discover.-of-', {
                    default: ' of ',
                  })}
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {Math.ceil(
                      $discoverQuery.data.count / discoverCampaignsPageSize,
                    )}
                  </span>
                </span>
              {/if}
            </div>
            {#if $discoverQuery.data?.data?.length}
              <div class="flex w-full items-center justify-center pt-4">
                {#if discoverCampaignsPage > 1}
                  <PaginationItem
                    normalClass="text-white bg-blue-600"
                    on:click={() => {
                      discoverCampaignsPage -= 1;
                    }}
                  >
                    {$_('components.discover.previous', {
                      default: 'Previous',
                    })}
                  </PaginationItem>
                {/if}
                {#if discoverCampaignsPage < Math.ceil($discoverQuery.data.count / discoverCampaignsPageSize)}
                  <PaginationItem
                    normalClass="text-white bg-blue-600"
                    on:click={() => {
                      discoverCampaignsPage += 1;
                    }}
                  >
                    {$_('components.discover.next', {
                      default: 'Next',
                    })}
                  </PaginationItem>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      </Card>
    </div>
  </TabItem>
  <TabItem
    open={openTab === 'my-campaigns'}
    title="My Campaigns"
    on:click={() => changeTab('my-campaigns')}
  >
    <div slot="title" class="flex items-center gap-2">
      <UserSolid size="sm" />
      My Campaigns
    </div>
    <div transition:fly class="-mx-4 flex flex-col gap-6 sm:mx-0">
      <Card size="xl" class="w-full max-w-none">
        <div class="mb-4 flex w-full flex-wrap justify-between gap-8">
          <div>
            <Heading level={3} class="text-xl font-bold">
              {$_('creatorConnections.myCampaigns.heading', {
                default: 'My Campaigns',
              })}
            </Heading>
            <p class="mb-2 text-sm text-gray-500">
              {$_('creatorConnections.myCampaigns.description', {
                default: 'Keep track of all your campaigns in one place!',
              })}
            </p>
            <div
              class="flex items-center justify-between rounded-full border border-gray-300 bg-gray-100 px-1 py-1"
            >
              <button
                on:click={() => updateCampaignStatusFilter(null)}
                class={cls('rounded-full px-3 py-1', {
                  'bg-blue-500 font-bold text-white':
                    campaignStatusFilter === null,
                })}
              >
                All
              </button>
              <button
                on:click={() => updateCampaignStatusFilter('LIVE')}
                class={cls('rounded-full px-3 py-1', {
                  'bg-blue-500 font-bold text-white':
                    campaignStatusFilter === 'LIVE',
                })}
              >
                Live
              </button>
              <button
                on:click={() => updateCampaignStatusFilter('ENDED')}
                class={cls('rounded-full px-3 py-1', {
                  'bg-blue-500 font-bold text-white':
                    campaignStatusFilter === 'ENDED',
                })}>Ended</button
              >
              <button
                on:click={() => updateCampaignStatusFilter('NOT_STARTED')}
                class={cls('rounded-full px-3 py-1', {
                  'bg-blue-500 font-bold text-white':
                    campaignStatusFilter === 'NOT_STARTED',
                })}>Upcoming</button
              >
            </div>
          </div>
          <div class="flex flex-col items-end gap-2">
            <Input
              size="lg"
              class="sm:w-[350px]"
              bind:value={myCampaignsPageSearch}
              on:input={onMyCampaignsPageSearch}
              placeholder="Search by Campaign, ASIN, or Brand"
            >
              <SearchOutline slot="left" class="h-5 w-5" />
            </Input>
          </div>
        </div>
        <div class="-mx-4 sm:-mx-6">
          <Table class="border-b">
            <TableHead>
              <TableHeadCell>
                {$_('creatorConnections.myCampaigns.table.campaignName', {
                  default: 'Campaign Name',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.myCampaigns.table.brand', {
                  default: 'Brand',
                })}
              </TableHeadCell>
              <TableHeadCell
                >{$_('creatorConnections.myCampaigns.table.image', {
                  default: 'Image',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.myCampaigns.table.date', {
                  default: 'Date',
                })}
              </TableHeadCell>
              <TableHeadCell>
                {$_('creatorConnections.myCampaigns.table.campaignStatus', {
                  default: 'Campaign Status',
                })}
              </TableHeadCell>
              <TableHeadCell>
                <div class="flex items-center gap-1">
                  <div>Budget Remaining</div>
                  <div>
                    <InfoCircleOutline class="h-3 w-3" />
                  </div>
                  <Tooltip
                    type="light"
                    class="absolute max-w-[30ch] normal-case"
                  >
                    The progress bar below updates daily and will show you how
                    much campaign budget in total is remaining - hover over for
                    more details!
                  </Tooltip>
                </div>
              </TableHeadCell>
              <TableHeadCell class="flex items-center">
                <span>
                  {$_('creatorConnections.myCampaigns.table.commRate', {
                    default: 'Comm. Rate',
                  })}
                </span>
                <button
                  name="name"
                  on:click={() => triggerSort('commissionRate')}
                  class={`ml-1 hover:text-buttonBlue ${sortKey === 'commissionRate' ? 'text-sm' : 'text-lg'} h-8 w-2 text-gray-500`}
                >
                  {#if sortKey === 'commissionRate' && sortDir === 'desc'}
                    &downarrow;
                  {:else if sortKey === 'commissionRate' && sortDir === 'asc'}
                    &uparrow;
                  {:else}
                    &updownarrow;
                  {/if}
                </button>
              </TableHeadCell>
              <TableHeadCell>Explore</TableHeadCell>
            </TableHead>
            <TableBody>
              {#if $myCampaignsQuery.isLoading}
                <TableBodyRow class="h-[200px]">
                  <ListPlaceholder
                    class="absolute h-[200px] w-full max-w-[unset] overflow-hidden rounded-none border-none"
                  />
                </TableBodyRow>
              {:else if !$myCampaignsQuery.data?.data?.length > 0}
                <TableBodyRow class="h-[200px]">
                  <div
                    class="absolute h-[200px] w-full bg-white text-center font-medium leading-[200px]"
                  >
                    <span>
                      {$_('creatorConnections.myCampaigns.table.empty', {
                        default:
                          "No campaigns found. Click the 'Discover' tab to explore available campaigns and start earning!",
                      })}
                    </span>
                  </div>
                </TableBodyRow>
              {:else}
                {#each $myCampaignsQuery.data.data as myCampaign}
                  <TableBodyRow>
                    <TableBodyCell
                      class="max-w-[300px] whitespace-normal text-wrap text-sm font-normal text-gray-500"
                    >
                      {myCampaign.campaign_name}
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {myCampaign.brand_name}
                    </TableBodyCell>
                    <TableBodyCell>
                      <img
                        src={myCampaign.image_url}
                        height="72"
                        width="72"
                        class="h-[72px] w-[72px] object-contain"
                        alt={myCampaign.brand_name}
                      />
                    </TableBodyCell>
                    <TableBodyCell
                      class="whitespace-nowrap text-sm font-normal text-gray-500"
                    >
                      {formatDate(new Date(myCampaign.campaign_start_date))}
                      -
                      {formatDate(new Date(myCampaign.campaign_end_date))}
                    </TableBodyCell>
                    <TableBodyCell>
                      <div>
                        <button
                          id={`status-${myCampaign}`}
                          class={cls(
                            'disabled',
                            'rounded-full px-4 py-1',
                            getStatus(myCampaign.campaign_status).btnClass,
                          )}
                          >{getStatus(myCampaign.campaign_status)
                            .status}</button
                        >
                        <Popover
                          placement="left"
                          triggerBy={`status-${myCampaign}`}
                          trigger="hover"
                          class="font-dark w-60 whitespace-normal text-base"
                        >
                          {getStatus(myCampaign.campaign_status).tip}<br />
                          <span class="text-sm italic" style="font-weight: bold"
                            >{getStatus(myCampaign.campaign_status).note}</span
                          >
                        </Popover>
                      </div>
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      <div class="group cursor-default pb-12">
                        <p
                          class="opacity-0 transition-all group-hover:opacity-100"
                        >
                          {new Intl.NumberFormat('en-US', {
                            currency: 'USD',
                            style: 'currency',
                            maximumFractionDigits: 0,
                          }).format(myCampaign.campaign_remaining_budget)}
                          /
                        </p>
                        <p
                          class="font-[900] opacity-0 transition-all group-hover:opacity-100"
                        >
                          {new Intl.NumberFormat('en-US', {
                            currency: 'USD',
                            style: 'currency',
                            maximumFractionDigits: 0,
                          }).format(myCampaign.campaign_budget)}
                        </p>
                        <div>
                          <Progressbar
                            progress={Math.floor(
                              (myCampaign.campaign_remaining_budget /
                                myCampaign.campaign_budget) *
                                100,
                            )}
                          />
                        </div>
                      </div>
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      {myCampaign.campaign_commission_rate}%
                    </TableBodyCell>
                    <TableBodyCell class="text-sm font-normal text-gray-500">
                      <Button
                        size="xs"
                        class="cursor:pointer flex gap-1"
                        disabled={myCampaign.campaign_status === 'ENDED'}
                        on:click={() => {
                          myCampaignsCreateLink(myCampaign.campaign_name);
                        }}
                      >
                        <span class="text-sm font-normal"> Go To ASINs </span>
                      </Button>
                    </TableBodyCell>
                  </TableBodyRow>
                {/each}
              {/if}
            </TableBody>
          </Table>
          <div class="flex w-full flex-col items-center justify-center py-5">
            <div class="flex items-center">
              <div class="mr-2 text-sm text-gray-700">
                {$_('components.myCampaigns.rows-per-page', {
                  default: 'Rows per page:',
                })}
              </div>
              <div class="mr-4">
                <Select
                  placeholder=""
                  size="sm"
                  class="mt-2"
                  items={rowsPerPageSelect}
                  bind:value={myCampaignsPageSize}
                />
              </div>
              <!-- Help text -->
              {#if $myCampaignsQuery.data?.data?.length}
                <span class="text-sm text-gray-700 dark:text-gray-400">
                  {$_('components.myCampaigns.showingPage-', {
                    default: 'Showing page ',
                  })}
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {myCampaignsPage}
                  </span>
                  {$_('components.myCampaigns.-of-', {
                    default: ' of ',
                  })}
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {Math.ceil(
                      $myCampaignsQuery.data.count / myCampaignsPageSize,
                    )}
                  </span>
                </span>
              {/if}
            </div>
            {#if $myCampaignsQuery.data?.data?.length}
              <div class="flex w-full items-center justify-center pt-4">
                {#if myCampaignsPage > 1}
                  <PaginationItem
                    normalClass="text-white bg-blue-600"
                    on:click={() => {
                      myCampaignsPage -= 1;
                    }}
                  >
                    {$_('components.myCampaigns.previous', {
                      default: 'Previous',
                    })}
                  </PaginationItem>
                {/if}
                {#if myCampaignsPage < Math.ceil($myCampaignsQuery.data.count / myCampaignsPageSize)}
                  <PaginationItem
                    normalClass="text-white bg-blue-600"
                    on:click={() => {
                      myCampaignsPage += 1;
                    }}
                  >
                    {$_('components.myCampaigns.next', {
                      default: 'Next',
                    })}
                  </PaginationItem>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      </Card>
    </div>
  </TabItem>
</Tabs>
